import type { JSX } from 'react';
import { scalePoint, scoreLabel, type Entry } from '@fatigue/shared';

/**
 * The pinned entry: score, name, capturedAt, receivedAt, device timezone and
 * the full comment.
 *
 * This is the ONLY place both timestamps appear together. They diverge when an
 * entry was queued offline and synced later, and a clinician reading "logged
 * at 3pm" needs to be able to discover it did not arrive until nine. The
 * divergence notice below is why the field is worth the space.
 */
export function EntryDetail({ entry }: { readonly entry: Entry | null }): JSX.Element {
  if (entry === null) {
    return <p className="empty">Click a point on the chart, or a comment, to see it here.</p>;
  }

  const point = scalePoint(entry.score);
  const captured = new Date(entry.capturedAt);
  const received = new Date(entry.receivedAt);
  // A few minutes covers ordinary latency; beyond that it was queued offline.
  const delayed = received.getTime() - captured.getTime() > 5 * 60 * 1000;

  return (
    <dl className="detail">
      <div className="detail__score">
        <span className="detail__swatch" style={{ background: point.colour }} />
        <span className="detail__name">{scoreLabel(entry.score)}</span>
      </div>

      <dt>Recorded by patient</dt>
      <dd>{formatFull(captured)}</dd>

      <dt>Received by server</dt>
      <dd>{formatFull(received)}</dd>

      {delayed && (
        <dd className="detail__note">
          Logged offline and synced later &mdash; the score describes when it was recorded, not
          when it arrived.
        </dd>
      )}

      <dt>Device timezone</dt>
      <dd>{entry.timezone}</dd>

      {entry.comment !== '' && (
        <>
          <dt>Comment</dt>
          <dd className="detail__comment">{entry.comment}</dd>
        </>
      )}
    </dl>
  );
}

const formatFull = (date: Date): string =>
  `${date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}, ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
